/**
 * Helpers for rendering a part's features in schema order.
 *
 * When a part's `part_type` matches an authored PartSchema, its features are
 * shown in the schema's defined order with human labels + units, and any
 * leftover free-form keys (not in the schema) are listed separately. Without a
 * matching schema, features fall back to the part's persisted `feature_order`.
 */

import type { PartSchemaFeature } from "../services/PartSchemaService";

export interface DisplayFeature {
  /** Canonical feature key as stored on the part. */
  key: string;
  /** Human-facing label (display_name + unit when schema-defined). */
  label: string;
  /** The part's value for this feature. */
  value: string;
}

export interface OrderedFeatures {
  /** Features defined by the schema, in schema position order. */
  schemaFeatures: DisplayFeature[];
  /** Leftover keys not defined by the schema, in feature_order. */
  extraFeatures: DisplayFeature[];
}

const featureLabel = (feature: PartSchemaFeature): string => {
  const base = feature.display_name || feature.canonical_name;
  return feature.unit ? `${base} (${feature.unit})` : base;
};

/**
 * Split + order a part's features against a schema's effective features.
 *
 * Schema features are emitted in `position` order (only those the part
 * actually has a value for); everything else is emitted in `featureOrder`,
 * then any remaining keys in object order.
 */
export function orderFeaturesBySchema(
  features: Record<string, string> | undefined,
  featureOrder: string[] | undefined,
  schemaFeatures: PartSchemaFeature[] | undefined,
): OrderedFeatures {
  const values = features ?? {};
  const allKeys = Object.keys(values);

  if (!schemaFeatures || schemaFeatures.length === 0) {
    const ordered = orderKeys(allKeys, featureOrder);
    return {
      schemaFeatures: [],
      extraFeatures: ordered.map((key) => ({
        key,
        label: key,
        value: String(values[key] ?? ""),
      })),
    };
  }

  const schemaKeys = new Set(schemaFeatures.map((f) => f.canonical_name));

  const sortedSchema = [...schemaFeatures].sort(
    (a, b) => a.position - b.position,
  );
  const schemaDisplay: DisplayFeature[] = sortedSchema
    .filter((f) => f.canonical_name in values)
    .map((f) => ({
      key: f.canonical_name,
      label: featureLabel(f),
      value: String(values[f.canonical_name] ?? ""),
    }));

  const leftoverKeys = orderKeys(
    allKeys.filter((k) => !schemaKeys.has(k)),
    featureOrder,
  );

  return {
    schemaFeatures: schemaDisplay,
    extraFeatures: leftoverKeys.map((key) => ({
      key,
      label: key,
      value: String(values[key] ?? ""),
    })),
  };
}

/** Order keys by the persisted feature_order, appending any unlisted keys. */
function orderKeys(
  keys: string[],
  featureOrder: string[] | undefined,
): string[] {
  if (!featureOrder || featureOrder.length === 0) return keys;
  const present = new Set(keys);
  const ordered = featureOrder.filter((k) => present.has(k));
  const remaining = keys.filter((k) => !ordered.includes(k));
  return [...ordered, ...remaining];
}
